<template>
  <section class="pricing-container" aria-labelledby="pricing-title">
    <div class="pricing-inner">
      <h2 id="pricing-title" class="title u-display">{{ $t('pricing.landingTitle') }}</h2>

      <!-- Pricing Toggle. aria-pressed carries the selected state: the active
           class is a purely visual cue, so without it a screen reader announces
           two plain buttons and never says which billing cycle is in effect. -->
      <div class="pricing-toggle">
        <button
          :class="['toggle-btn', { active: billingCycle === 'monthly' }]"
          :aria-pressed="billingCycle === 'monthly'"
          @click="billingCycle = 'monthly'"
        >
          {{ $t('pricing.monthly') }}
        </button>
        <button
          :class="['toggle-btn', { active: billingCycle === 'yearly' }]"
          :aria-pressed="billingCycle === 'yearly'"
          @click="billingCycle = 'yearly'"
        >
          {{ $t('pricing.yearly') }}
        </button>
      </div>

      <div class="card-container">
        <div class="pricing-card" v-for="plan in currentPlans" :key="plan.nameKey">
          <h3 class="plan-name">{{ $t(plan.nameKey) }}</h3>
          <div class="price-section">
            <transition name="price" mode="out-in">
              <div class="price-wrapper" :key="plan.isFree ? 'free' : billingCycle">
                <span class="price">${{ plan.price }}</span>
                <span class="per-month">
                  {{ plan.isFree ? $t('pricing.foreverFree') : $t('pricing.perMonth') }}
                </span>
                <div v-if="!plan.isFree && billingCycle === 'yearly'" class="yearly-savings">
                  <div class="billed-annually">
                    {{ $t('pricing.billedAnnuallyAt', { amount: plan.yearlyTotal }) }}
                  </div>
                  <div class="months-free">
                    <span class="highlight">{{ $t('pricing.monthsFree', { count: plan.yearlyMonthsFree }) }}</span>
                    <span class="savings-badge">{{ $t('pricing.savePercent', { percent: Math.round((plan.yearlyMonthsFree / 12) * 100) }) }}</span>
                  </div>
                </div>
              </div>
            </transition>
          </div>
          <div class="features-list">
            <div class="feature">
              <font-awesome-icon icon="comments" class="feature-icon" />
              <span class="feature-text">
                {{ plan.isFree
                  ? $t('pricing.freeChatsToStart', { count: plan.chats })
                  : $t('pricing.chatsPerMonth', { count: plan.chats }) }}
              </span>
            </div>
            <div class="feature">
              <font-awesome-icon icon="graduation-cap" class="feature-icon" />
              <span class="feature-text">
                {{ plan.isFree
                  ? $t('pricing.lifetimeLesson', { count: plan.lessons })
                  : $t('pricing.lessonsPerMonth', { count: plan.lessons }) }}
              </span>
            </div>
            <div class="feature" v-if="plan.multiLingual">
              <font-awesome-icon icon="language" class="feature-icon" />
              <span class="feature-text">
                {{ $t('pricing.multiLingualAccess') }}
              </span>
            </div>
          </div>
          <button class="subscribe-btn" @click="handleSubscribe(plan)">
            {{ plan.isFree ? $t('pricing.tryForFree') : $t('pricing.subscribe') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { authUtils } from "@/scripts/authUtils.js";
import { authStore } from "@/store/auth.js";
import { pricingStore } from "@/store/pricing.js";
import { STORAGE_KEYS } from "@/constants/storageKeys.js";

export default {
  data() {
    return {
      billingCycle: "monthly",
    };
  },
  computed: {
    currentPlans() {
      const free = pricingStore.getPlanDetailsById("free");
      const paidPlans = [
        pricingStore.getPlanDetailsById("foundation"),
        pricingStore.getPlanDetailsById("fluency"),
      ];

      return [
        {
          nameKey: free.nameKey,
          price: 0,
          isFree: true,
          chats: free.lifetimeChats,
          lessons: free.lifetimeLessons,
        },
        ...paidPlans.map((plan) => ({
          nameKey: plan.nameKey,
          isFree: false,
          price:
            this.billingCycle === "monthly"
              ? plan.monthlyPrice
              : plan.yearlyPrice,
          yearlyTotal: plan.yearlyTotal,
          yearlyMonthsFree: plan.yearlyMonthsFree,
          chats: plan.chatsPerMonth,
          lessons: plan.lessonsPerMonth,
          multiLingual: plan.multiLingual,
          lookupKey:
            this.billingCycle === "monthly"
              ? plan.monthlyLookupKey
              : plan.yearlyLookupKey,
          iapBasePlanId:
            pricingStore.getBasePlanId(
              plan.id,
              this.billingCycle
            ),
        })),
      ];
    },
  },
  methods: {
    handleSubscribe(plan) {
      // Free plan needs no pre-selected subscription — user just signs up.
      if (!plan.isFree) {
        localStorage.setItem(
          STORAGE_KEYS.PENDING_SUBSCRIPTION,
          JSON.stringify(plan)
        );
      }
      authUtils.startAuth();
    },
  },
};
</script>

<style scoped>
/* Marketing routes are light-mode only (the pre-paint stamp in
   public/index.html gates dark on /dashboard/ and native), so this section has
   no dark-mode branch and stays on the hue tokens the sibling landing sections
   use rather than the role-token layer. */

/* width: 100%, never 100vw: 100vw includes the scrollbar gutter, which is what
   put a horizontal scrollbar on this page in the first place. This was the last
   of the four offenders.

   The band is white while TestimonialsSection directly above is --al-bg-light,
   so the two sections keep a visible boundary. It is a full-bleed band now
   rather than a rounded floating slab, so it no longer carries a border-radius
   of its own; the cards inside keep theirs. */
.pricing-container {
  background-color: var(--al-white);
  position: relative;
  width: 100%;
}

.pricing-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 72px 32px;
  text-align: center;
}

/* DESIGN.md type scale, H2: 26px / 1.16 / 700. It is not breakpoint-split, so
   there is no mobile size override below. */
.title {
  font-size: 26px;
  /* public/index.html loads Fraunces at discrete 600/700/800. */
  font-weight: 700;
  line-height: 1.16;
  color: var(--al-ink-900);
  text-align: center;
  margin: 0 0 32px;
  overflow-wrap: break-word;
}

.pricing-toggle {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 40px;
}

.toggle-btn {
  padding: 8px 24px;
  border: none;
  border-radius: var(--al-radius-pill);
  background-color: var(--al-gray-200);
  color: var(--al-ink-900);
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: background-color var(--al-dur-short) var(--al-ease-enter);
}

.toggle-btn.active {
  /* AA: white on --al-primary is ~3.3:1, which only clears the bar as WCAG
     large text (>= 18.66px bold); this label is 16px. --al-primary-hover
     passes at ~4.67:1, the same substitution the testimonial avatar makes. */
  background-color: var(--al-primary-hover);
  color: var(--al-white);
}

.toggle-btn:focus-visible {
  outline: 2px solid var(--al-ink-600);
  outline-offset: 2px;
}

.card-container {
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 24px;
}

/* DESIGN.md's card recipe: white surface, subtle --al-gray-300 hairline, small
   shadow, radius 16px. */
.pricing-card {
  background: var(--al-white);
  border: 1px solid var(--al-gray-300);
  border-radius: var(--al-radius-lg);
  box-shadow: var(--al-shadow-sm);
  padding: 32px;
  position: relative;
  display: flex;
  flex-direction: column;
  /* Equal columns; min-width: 0 so a long plan name cannot force a track wider
     than its share and push the row past the container. */
  flex: 1 1 0;
  min-width: 0;
  overflow-wrap: break-word;
}

/* DESIGN.md type scale, H3: 20px / 1.24 / 700. */
.plan-name {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.24;
  color: var(--al-ink-900);
  margin: 0 0 20px;
}

.price-section {
  margin-bottom: 0;
}

.price-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* DESIGN.md type scale, Display: 40px / 1.05. */
.price {
  font-size: 40px;
  font-weight: 700;
  line-height: 1.05;
  color: var(--al-ink-900);
}

.per-month {
  color: var(--al-gray);
  font-size: 16px;
}

.yearly-savings {
  margin-top: 8px;
}

/* flex-wrap so the "N months free" phrase and the savings badge stack instead
   of colliding: both are localized strings of unpredictable length and the card
   is only ~240px of content wide at the narrowest three-up viewport. */
.months-free {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  font-weight: 600;
}

.highlight {
  /* Orange text on white is ~2.6:1 (fails AA); ink-900 keeps the emphasis
     while the badge below carries the warm accent via its background. */
  color: var(--al-ink-900);
  font-size: 1rem;
}

.savings-badge {
  background-color: var(--al-warm-soft);
  /* Orange text on warm-soft is ~2:1 (fails AA); ink-900 is the required
     ink/gray-700-level darkness for text on warm-soft backgrounds. */
  color: var(--al-ink-900);
  padding: 4px 8px;
  border-radius: var(--al-radius-pill);
  font-size: 0.875rem;
  font-weight: 600;
}

.billed-annually {
  color: var(--al-gray);
  font-size: 14px;
}

.subscribe-btn {
  /* DESIGN.md specifies the primary button as solid --al-primary with white
     text. That pairing is ~3.3:1, so it only clears AA as WCAG large text
     (>= 18.66px bold): the label is 19px/700 for exactly that reason, the same
     resolution HeroSection's CTA uses. Do not shrink it. */
  background-color: var(--al-primary);
  color: var(--al-white);
  border: none;
  border-radius: var(--al-radius);
  padding: 12px 24px;
  font-size: 19px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  min-height: 48px;
  /* Cards differ in height by feature count; this keeps the buttons on one
     baseline instead of floating mid-card. */
  margin-top: auto;
  transition: background-color var(--al-dur-short) var(--al-ease-enter);
}

.subscribe-btn:hover {
  background-color: var(--al-primary-hover);
}

.subscribe-btn:focus-visible {
  outline: 2px solid var(--al-ink-600);
  outline-offset: 2px;
}

/* Price change animation */
.price-enter-active,
.price-leave-active {
  transition: all 0.3s ease;
}

.price-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.price-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.price-enter-to,
.price-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.features-list {
  margin: 24px 0;
  padding: 16px;
  background-color: var(--al-bg-light);
  border-radius: var(--al-radius);
}

/* DESIGN.md type scale, Body: 16px / 1.55 / 400. */
.feature {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  color: var(--al-gray-700);
  font-size: 16px;
  line-height: 1.55;
}

.feature:not(:last-child) {
  border-bottom: 1px solid var(--al-gray-300);
}

.feature-icon {
  color: var(--al-primary);
  font-size: 1.1rem;
  width: 20px;
  flex-shrink: 0;
}

.feature-text {
  text-align: left;
}

/* Tablet padding matches HeroSection's and the sibling landing sections'. The
   cards stack here rather than at 767px: three columns inside a 768px viewport
   leave each card ~150px of text, which is where the feature rows start to
   wrap to three lines in the longer locales. */
@media (max-width: 1023px) {
  .pricing-inner {
    padding: 56px 24px;
  }

  .card-container {
    flex-direction: column;
    align-items: center;
  }

  .pricing-card {
    flex: 0 1 auto;
    width: 100%;
    max-width: 420px;
  }
}

@media (max-width: 767px) {
  .pricing-inner {
    padding: 48px 16px;
  }

  .title {
    margin-bottom: 24px;
  }

  .pricing-toggle {
    margin-bottom: 28px;
  }

  .pricing-card {
    padding: 24px;
  }
}
</style>
